import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calculator, TrendingUp, TrendingDown, Minus } from "lucide-react";

const K_FACTOR = 32; // Standard K-factor for calculating ELO changes

function calculateExpectedScore(playerElo: number, opponentElo: number): number {
  return 1 / (1 + Math.pow(10, (opponentElo - playerElo) / 400));
}

function calculateEloChange(playerElo: number, opponentElo: number, result: 'win' | 'draw' | 'loss'): number {
  const expected = calculateExpectedScore(playerElo, opponentElo);
  const actual = result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;
  return Math.round(K_FACTOR * (actual - expected));
}

function getSkillLabel(elo: number): { label: string; emoji: string; color: string } {
  if (elo < 800) return { label: "Beginner", emoji: "🐣", color: "teen-green" };
  if (elo < 1200) return { label: "Intermediate", emoji: "🎯", color: "teen-cyan" };
  if (elo < 1600) return { label: "Advanced", emoji: "⚡", color: "teen-blue" };
  if (elo < 2000) return { label: "Expert", emoji: "🔥", color: "teen-purple" };
  return { label: "Master", emoji: "👑", color: "teen-gold" };
}

export function EloCalculatorWidget() {
  const [playerElo, setPlayerElo] = useState(1200);
  const [opponentElo, setOpponentElo] = useState(1200);

  const winChange = calculateEloChange(playerElo, opponentElo, 'win');
  const drawChange = calculateEloChange(playerElo, opponentElo, 'draw');
  const lossChange = calculateEloChange(playerElo, opponentElo, 'loss');
  const winProbability = Math.round(calculateExpectedScore(playerElo, opponentElo) * 100);

  const eloDiff = playerElo - opponentElo;
  const matchupLabel = eloDiff > 100 
    ? { text: "You're the favorite! 👑", color: "teen-gold" }
    : eloDiff < -100 
    ? { text: "You're the underdog! 🐕 A win is worth more!", color: "teen-purple" }
    : { text: "Even match! 🤝", color: "teen-cyan" };

  const playerSkill = getSkillLabel(playerElo);
  const opponentSkill = getSkillLabel(opponentElo);

  const handlePreset = (preset: 'same' | 'underdog' | 'favorite') => {
    switch (preset) {
      case 'same':
        setPlayerElo(1200);
        setOpponentElo(1200);
        break;
      case 'underdog':
        setPlayerElo(1000);
        setOpponentElo(1400);
        break;
      case 'favorite':
        setPlayerElo(1400);
        setOpponentElo(1000);
        break;
    }
  };

  return (
    <Card className="bg-gradient-to-br from-teen-cyan/5 to-teen-purple/5 border-teen-cyan/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-teen-cyan" />
          Try the ELO Calculator! 🔢
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm">
          Move the sliders to see how wins, draws, and losses affect your rating!
        </p>

        {/* Player ELO Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Your Rating:</span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`border-${playerSkill.color}/30 text-${playerSkill.color}`}>
                {playerSkill.emoji} {playerSkill.label}
              </Badge>
              <span className="text-lg font-bold text-teen-cyan">{playerElo}</span>
            </div>
          </div>
          <Slider
            value={[playerElo]}
            onValueChange={(value) => setPlayerElo(value[0])}
            min={400}
            max={3000}
            step={50}
            className="w-full"
          />
        </div>

        {/* Opponent ELO Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Opponent Rating:</span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`border-${opponentSkill.color}/30 text-${opponentSkill.color}`}>
                {opponentSkill.emoji} {opponentSkill.label}
              </Badge>
              <span className="text-lg font-bold text-teen-purple">{opponentElo}</span>
            </div>
          </div>
          <Slider
            value={[opponentElo]}
            onValueChange={(value) => setOpponentElo(value[0])}
            min={400}
            max={3000}
            step={50}
            className="w-full"
          />
        </div>

        {/* Quick Presets */}
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePreset('same')}
            className="text-xs"
          >
            🤝 Same Skill
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePreset('underdog')}
            className="text-xs"
          >
            🐕 Underdog
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePreset('favorite')}
            className="text-xs"
          >
            👑 Favorite
          </Button>
        </div>

        {/* Win Probability Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Win Probability:</span>
            <span className="font-bold text-teen-green">{winProbability}%</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-teen-green to-teen-cyan transition-all duration-300"
              style={{ width: `${winProbability}%` }}
            />
          </div>
          <p className={`text-sm text-center font-medium text-${matchupLabel.color}`}>
            {matchupLabel.text}
          </p>
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-lg bg-teen-green/10 border border-teen-green/20 text-center">
            <TrendingUp className="h-5 w-5 text-teen-green mx-auto mb-1" />
            <p className="text-xs text-muted-foreground mb-1">🏆 Win</p>
            <p className={`text-xl font-bold ${winChange >= 0 ? 'text-teen-green' : 'text-red-500'}`}>
              {winChange >= 0 ? '+' : ''}{winChange}
            </p>
          </div>
          
          <div className="p-4 rounded-lg bg-teen-cyan/10 border border-teen-cyan/20 text-center">
            <Minus className="h-5 w-5 text-teen-cyan mx-auto mb-1" />
            <p className="text-xs text-muted-foreground mb-1">🤝 Draw</p>
            <p className={`text-xl font-bold ${drawChange >= 0 ? 'text-teen-cyan' : 'text-red-500'}`}>
              {drawChange >= 0 ? '+' : ''}{drawChange}
            </p>
          </div>
          
          <div className="p-4 rounded-lg bg-teen-pink/10 border border-teen-pink/20 text-center">
            <TrendingDown className="h-5 w-5 text-teen-pink mx-auto mb-1" />
            <p className="text-xs text-muted-foreground mb-1">📉 Loss</p>
            <p className="text-xl font-bold text-teen-pink">
              {lossChange}
            </p>
          </div>
        </div>

        <div className="p-3 bg-teen-gold/10 rounded-lg border border-teen-gold/20">
          <p className="text-xs text-center text-muted-foreground">
            <strong className="text-teen-gold">💡 Pro Tip:</strong> Beating a higher-rated opponent gives you more points!
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
